import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Mail,
  Plus,
  Send,
  Shield,
  Users,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { StatusBanner } from "@/components/ui/status-banner";
import {
  grantAccess,
  inviteRecruiter,
  listTeamMembers,
  revokeAccess,
  setAdmin,
} from "@/lib/team";
import { useActiveClientId } from "@/hooks/useActiveClient";
import { supabase } from "@/lib/supabase";
import type { Company, TeamMember } from "@/lib/types";
import { NoClientPicked } from "@/components/NoClientPicked";

export const Route = createFileRoute("/team")({
  component: TeamPage,
});

function TeamPage() {
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para gestionar el equipo.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <TeamContent />
        </AppShell>
      )}
    </LoginGate>
  );
}

function TeamContent() {
  const qc = useQueryClient();
  const [activeClientId] = useActiveClientId();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error" | "info";
    title: string;
    detail?: string;
  } | null>(null);

  const { data: allMembers = [], isLoading } = useQuery({
    queryKey: ["team-members"],
    queryFn: listTeamMembers,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["companies", "for-team"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Pick<Company, "id" | "name">[];
    },
  });

  // Filtramos a los miembros que tienen acceso al cliente activo. Admins
  // siempre aparecen (porque ven todo).
  const members = useMemo(() => {
    if (!activeClientId) return allMembers;
    return allMembers.filter(
      (m) => m.is_admin || m.company_ids.includes(activeClientId)
    );
  }, [allMembers, activeClientId]);

  const activeCompanyName = companies.find((c) => c.id === activeClientId)?.name;

  const isEmpty = !isLoading && members.length === 0;

  if (!activeClientId) {
    return (
      <NoClientPicked
        title="Elegí un cliente para ver su equipo"
        description="Cada cliente tiene su propio equipo de reclutadores. Elegí uno desde el dropdown de arriba a la izquierda."
      />
    );
  }

  return (
    <div className="px-6 py-5">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Equipo {activeCompanyName && (
              <span className="text-muted-foreground font-normal">
                · {activeCompanyName}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Reclutadores con acceso a este cliente. Los admins también ven todo.
          </p>
        </div>
        <Button variant="primary" onClick={() => setInviteOpen(true)}>
          <Plus className="h-4 w-4" />
          Invitar reclutador
        </Button>
      </div>

      {feedback && (
        <div className="mb-6">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isEmpty ? (
        <Card className="p-10 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-4">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-base font-semibold tracking-tight mb-1">
            Sin miembros del equipo
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Si sos admin, podés invitar al primer reclutador. Si no ves a nadie
            (incluso a vos mismo), todavía no fuiste seteado como admin —
            avisame y te pasamos el bootstrap SQL.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                companies={companies}
                onChange={() => {
                  qc.invalidateQueries({ queryKey: ["team-members"] });
                  qc.invalidateQueries({ queryKey: ["companies"] });
                }}
                onFeedback={setFeedback}
              />
            ))}
          </ul>
        </Card>
      )}

      <InviteRecruiterDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        companies={companies}
        defaultCompanyId={activeClientId}
        onSuccess={(detail) => {
          setFeedback({ kind: "success", title: "Invitación enviada", detail });
          qc.invalidateQueries({ queryKey: ["team-members"] });
        }}
      />
    </div>
  );
}

// =================== MEMBER ROW ===================
interface MemberRowProps {
  member: TeamMember;
  companies: Pick<Company, "id" | "name">[];
  onChange: () => void;
  onFeedback: (f: {
    kind: "success" | "error";
    title: string;
    detail?: string;
  }) => void;
}

function MemberRow({ member, companies, onChange, onFeedback }: MemberRowProps) {
  const [editing, setEditing] = useState(false);
  return (
    <li className="px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-secondary border border-border flex items-center justify-center text-[11px] font-semibold shrink-0">
          {(member.email ?? "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{member.email}</span>
            {member.is_admin && (
              <Badge variant="primary" className="text-[9px]">
                <Shield className="h-2.5 w-2.5 mr-0.5" />
                Admin
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {member.is_admin
              ? "Ve todos los clientes"
              : member.company_ids.length === 0
                ? "Sin acceso a clientes"
                : `${member.company_ids.length} ${member.company_ids.length === 1 ? "cliente" : "clientes"} asignado${member.company_ids.length === 1 ? "" : "s"}`}
            {member.last_sign_in_at && (
              <span className="ml-2">
                · último login {formatRelative(member.last_sign_in_at)}
              </span>
            )}
          </div>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => setEditing(!editing)}
        >
          {editing ? "Cerrar" : "Gestionar acceso"}
        </Button>
      </div>

      {editing && (
        <MemberAccessEditor
          member={member}
          companies={companies}
          onChange={onChange}
          onFeedback={onFeedback}
        />
      )}
    </li>
  );
}

function MemberAccessEditor({
  member,
  companies,
  onChange,
  onFeedback,
}: MemberRowProps) {
  const [pending, setPending] = useState<string | null>(null);

  async function toggleAccess(companyId: string, checked: boolean) {
    setPending(companyId);
    try {
      if (checked) {
        await grantAccess(member.id, companyId);
      } else {
        await revokeAccess(member.id, companyId);
      }
      onChange();
    } catch (e) {
      onFeedback({
        kind: "error",
        title: "No se pudo actualizar acceso",
        detail: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setPending(null);
    }
  }

  async function toggleAdmin() {
    setPending("__admin__");
    try {
      await setAdmin(member.id, !member.is_admin);
      onChange();
      onFeedback({
        kind: "success",
        title: member.is_admin
          ? `${member.email} ya no es admin`
          : `${member.email} ahora es admin`,
      });
    } catch (e) {
      onFeedback({
        kind: "error",
        title: "No se pudo cambiar admin",
        detail: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="mt-4 ml-12 pl-3 border-l-2 border-border">
      <div className="space-y-3">
        <div className="flex items-center gap-2 pb-3 border-b border-border">
          <Shield className="h-3.5 w-3.5 text-primary" />
          <Label className="cursor-pointer flex items-center gap-2 flex-1">
            <Checkbox
              checked={member.is_admin}
              disabled={pending === "__admin__"}
              onCheckedChange={toggleAdmin}
            />
            <span className="text-sm">
              Es <strong>admin</strong>
              <span className="text-muted-foreground ml-1 text-[11px]">
                — ve todos los clientes, puede crear y borrar, gestiona el equipo
              </span>
            </span>
          </Label>
        </div>

        {!member.is_admin && (
          <>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Clientes con acceso
            </div>
            {companies.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">
                No hay clientes cargados todavía.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {companies.map((c) => {
                  const has = member.company_ids.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-accent/50 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={has}
                        disabled={pending === c.id}
                        onCheckedChange={(v) => toggleAccess(c.id, !!v)}
                      />
                      <span className="truncate">{c.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =================== INVITE DIALOG ===================
function InviteRecruiterDialog({
  open,
  onClose,
  companies,
  defaultCompanyId,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  companies: Pick<Company, "id" | "name">[];
  defaultCompanyId: string | null;
  onSuccess: (detail: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(
    defaultCompanyId ? [defaultCompanyId] : []
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setSelectedIds(defaultCompanyId ? [defaultCompanyId] : []);
      setError(null);
    } else {
      // Cada vez que abrís el dialog, pre-seleccionamos el cliente activo.
      setSelectedIds(defaultCompanyId ? [defaultCompanyId] : []);
    }
  }, [open, defaultCompanyId]);

  const mutation = useMutation({
    mutationFn: () =>
      inviteRecruiter({
        email: email.trim(),
        companyIds: selectedIds,
      }),
    onSuccess: (res) => {
      onSuccess(
        res.isNewUser
          ? `Le enviamos un email a ${email.trim()} con el link para activar la cuenta. Tiene acceso a ${res.grantedCompanies} ${res.grantedCompanies === 1 ? "cliente" : "clientes"}.`
          : `${email.trim()} ya existía. Le sumamos acceso a ${res.grantedCompanies} ${res.grantedCompanies === 1 ? "cliente nuevo" : "clientes nuevos"}.`
      );
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const canSubmit =
    email.trim().length > 0 &&
    /.+@.+\..+/.test(email.trim()) &&
    !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Invitar reclutador
          </DialogTitle>
          <DialogDescription>
            Le mandamos un email con un link para que active su cuenta. Después
            podés modificar el acceso desde la lista.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <StatusBanner variant="error" title="Error" onDismiss={() => setError(null)}>
            {error}
          </StatusBanner>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="juan@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>Clientes a los que tendrá acceso</Label>
          {companies.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              Todavía no cargaste clientes. Podés invitar igual y darle acceso
              después.
            </p>
          ) : (
            <div className="max-h-[200px] overflow-auto border border-border rounded-md p-2 space-y-0.5">
              {companies.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent/50 cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={selectedIds.includes(c.id)}
                    onCheckedChange={(v) =>
                      setSelectedIds((prev) =>
                        v ? [...prev, c.id] : prev.filter((id) => id !== c.id)
                      )
                    }
                  />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!canSubmit}
          >
            <Send className="h-4 w-4" />
            Enviar invitación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================== HELPERS ===================
function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1) {
    const hrs = Math.floor(diff / (1000 * 60 * 60));
    if (hrs < 1) return "hace minutos";
    return `hace ${hrs}h`;
  }
  if (days < 7) return `hace ${days}d`;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

